import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { filtersToPersistAtom } from '@/atoms/filters/persistedFilters';
import { filterSetsAtom } from '@/atoms/queries/userConfigs';
import {
  iSaveFilterSetModalOpenAtom,
  resetUserConfigActionStateAtom,
  userConfigActionAtom,
  userConfigIdAtom,
  userConfigNameAtom,
} from '@/atoms/userConfigs';
import { useUpsertFilterSetConfig } from '@/hooks/useUpsertFilterSetConfig';
import { UserConfigActions } from '@/types/userConfigs';
import { GenericSaveModal } from '../GenericSaveModal/GenericSaveModal';

/**
 * This modal is used for:
 * - Saving a brand new filter set (which will create a new row in the user config table)
 * - Saving changes to an existing filter set under a new name (which will create a new row in the user config table)
 */
const SaveFilterModalWithData = () => {
  const { t } = useTranslation(['general', 'filters']);
  const [userConfigName, setUserConfigName] = useAtom(userConfigNameAtom);
  const resetUserConfigActionState = useSetAtom(resetUserConfigActionStateAtom);

  const filterSets = useAtomValue(filterSetsAtom);
  const filtersToPersist = useAtomValue(filtersToPersistAtom);

  const userConfigAction = useAtomValue(userConfigActionAtom);
  const configIdToAmend = useAtomValue(userConfigIdAtom);
  const filtersToSave = configIdToAmend
    ? filterSets.find(({ configId }) => configId === configIdToAmend)?.definition.filters
    : filtersToPersist;

  const upsertFilterSetConfig = useUpsertFilterSetConfig();
  const otherFilterSetNames = filterSets
    .filter(({ configId }) => configId !== configIdToAmend)
    .map(({ definition }) => definition.titleSettings.title);

  const handleCancel = () => resetUserConfigActionState();

  const handleConfirm = async () => {
    const configId = userConfigAction === UserConfigActions.DUPLICATE ? undefined : configIdToAmend ?? undefined;
    await upsertFilterSetConfig(userConfigName?.trim(), filtersToSave, configId);

    /* cleanup */
    handleCancel();
  };

  const modalTitle = t(`saveFilterModalTitle.${userConfigAction}`, {
    ns: 'modal',
  });

  return (
    <GenericSaveModal
      id={`${userConfigAction}-filter-set-title`}
      title={modalTitle}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      confirmLabel={t('save')}
      value={userConfigName ?? ''}
      onChange={setUserConfigName}
      existingConfigNames={otherFilterSetNames}
    />
  );
};

export const SaveFilterModal = () => {
  const isOpenSaveFilterSetModal = useAtomValue(iSaveFilterSetModalOpenAtom);

  return (
    isOpenSaveFilterSetModal && (
      <Suspense>
        <SaveFilterModalWithData />
      </Suspense>
    )
  );
};
